import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  InputBase,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BadgeIcon from "@mui/icons-material/Badge";

import { CartContext } from "../../context/CartContext";
import api from "../../api/axios";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useContext(CartContext);

  const [query, setQuery] = useState("");
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem("user");
    setUser(saved ? JSON.parse(saved) : null);
    // Reset menu state when component mounts
    setMenuOpen(false);
    setAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => {
  if (!query.trim()) {
    setSuggestions([]);
    setCategories([]);
    return;
  }

  const delay = setTimeout(() => {
    // fetch(`http://localhost:5000/api/search/suggest?q=${query}`)
    fetch(`https://api.monkeye.in/api/search/suggest?q=${query}`)
      .then(res => res.json())
      .then(data => {
        setSuggestions(data.products || []);
        setCategories(data.categories || []);
        setShowDropdown(true);
      });
  }, 300); // debounce

  return () => clearTimeout(delay);
}, [query]);

// useEffect(() => {
//   const handleClick = () => setShowDropdown(false);
//   window.addEventListener("click", handleClick);
//   return () => window.removeEventListener("click", handleClick);
// }, []);

useEffect(() => {
  const handleClick = (e) => {
    if (!e.target.closest("#search-box")) {
      setShowDropdown(false);
    }
  };

  window.addEventListener("click", handleClick);
  return () => window.removeEventListener("click", handleClick);
}, []);

  const role = user?.role;

  const dashboardRoute =
    role === "customer"
      ? "/customer/dashboard"
      : role === "vendor"
      ? "/vendor/dashboard"
      : role === "agent"
      ? "/agent/dashboard"
      : "/customer/dashboard";

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const getProfileRoute = (role) => {
    switch (role) {
      case "customer":
        return "/register/customer";
      case "vendor":
        return "/register/vendor";
      case "agent":
        return "/register/agent";
      case "admin":
        return "/register/admin";
      default:
        return "/login";
    }
  };

  const SearchBox = (
  <Box sx={{ position: "relative", width: "100%", maxWidth: 800 }}>
    <Paper
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        setShowDropdown(false);
        navigate(`/search?q=${query}`);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        height: 40,
      }}
    >
      <InputBase
        placeholder="Search Pepper Diamond"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query && setShowDropdown(true)}
        sx={{ ml: 2, flex: 1 }}
      />
      <IconButton type="submit" sx={{ bgcolor: "#ff9900", px: 2 }}>
        <SearchIcon sx={{ color: "#fff" }} />
      </IconButton>
    </Paper>

    {/* 🔥 AUTOCOMPLETE DROPDOWN */}
    {showDropdown && (suggestions.length > 0 || categories.length > 0) && (
      <Paper
        sx={{
          position: "absolute",
          top: 45,
          width: "100%",
          zIndex: 2000,
          maxHeight: 400,
          overflowY: "auto",
        }}
      >
        {/* Categories */}
        {categories.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Categories
            </Box>
            {categories.map((cat) => (
              <Box
                key={cat._id}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
                onClick={() => {
                  setShowDropdown(false);
                  navigate(`/search?q=${cat.name}`);
                }}
              >
                🔎 {cat.name}
              </Box>
            ))}
          </>
        )}

        {/* Products */}
        {suggestions.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Products
            </Box>
            {suggestions.map((p) => (
              <Box
                key={p._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
                onClick={() => {
                  setShowDropdown(false);
                  navigate(`/product/${p._id}`);
                }}
              >
     <img
      src={p.thumbnailUrl || "/placeholder-product.png"}
      width={40}
      height={40}
      style={{ objectFit: "cover", borderRadius: 4 }}
      alt={p.title}
    />
                {p.title}
              </Box>
            ))}
          </>
        )}
      </Paper>
    )}
  </Box>
);

  return (
    <AppBar position="fixed" sx={{ bgcolor: "#131921", zIndex: 1300 }}>
      {/* ================= ROW 1 ================= */}
      <Toolbar
        sx={{
          minHeight: 64,
          display: "flex",
          gap: 2,
        }}
      >
        {/* LOGO */}
        <Typography
          component={Link}
          to="/"
          sx={{
            color: "#fff",
            textDecoration: "none",
            fontWeight: 700,
            fontSize: 20,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "#aaa", marginRight: 6 }}>Pepper</span>Diamond
        </Typography>

        {/* 🔍 SEARCH (DESKTOP ONLY) */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
          }}
        >
          {SearchBox}
        </Box>

        {/* ICONS */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,ml: "auto", }}>
          {user && (
            <Tooltip title="Dashboard">
              <IconButton component={Link} to={dashboardRoute} color="inherit">
                <DashboardIcon />
              </IconButton>
            </Tooltip>
          )}

          {role === "admin" && (
            <>
              <IconButton component={Link} to="/register/admin" color="inherit">
                <AdminPanelSettingsIcon />
              </IconButton>
              <IconButton component={Link} to="/register/vendor" color="inherit">
                <StorefrontIcon />
              </IconButton>
              <IconButton component={Link} to="/register/agent" color="inherit">
                <BadgeIcon />
              </IconButton>
            </>
          )}

          {role === "customer" && (
            <IconButton component={Link} to="/cart" color="inherit">
              <ShoppingCartIcon />
              {cartCount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "red",
                    color: "#fff",
                    borderRadius: "50%",
                    px: 0.6,
                    fontSize: 11,
                  }}
                >
                  {cartCount}
                </Box>
              )}
            </IconButton>
          )}

          {user ? (
            <>
              <IconButton 
                color="inherit" 
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setMenuOpen(true);
                }}
              >
                <PersonIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={menuOpen && Boolean(anchorEl)}
                onClose={() => {
                  setMenuOpen(false);
                  setAnchorEl(null);
                }}
                MenuListProps={{
                  onMouseLeave: () => {
                    setMenuOpen(false);
                    setAnchorEl(null);
                  }
                }}
              >
                <MenuItem
                  onClick={() => {
                    navigate(getProfileRoute(role));
                    setMenuOpen(false);
                    setAnchorEl(null);
                  }}
                >
                  My Profile
                </MenuItem>
                <MenuItem 
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                    setAnchorEl(null);
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton component={Link} to="/login" color="inherit">
              <AccountCircleIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* ================= ROW 2 (MOBILE/TABLET SEARCH) ================= */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          px: 2,
          pb: 1.5,
          justifyContent: "center",
        }}
      >
        {SearchBox}
      </Box>
    </AppBar>
  );
};

export default Header;
